import type { CostType, CostTypeCategory } from "@repo/shared/types";
import { AlertCircle, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableActionsHead,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { type ApiCostValue, computeApiCostValues } from "@/lib/utils/compute-api-cost-values";
import { CostRow, type CostValueEntry } from "./cost-row";
import { T } from "./product-costs-display.i18n";

type ProductCostsDisplayProps = {
  ticker: string | null;
  dataset: string | null;
  costTypes: CostType[];
  costValues: Record<string, CostValueEntry>;
  onCostValuesChange: (values: Record<string, CostValueEntry>) => void;
  onNavigateToGeneral: () => void;
  portfolioCollectionId?: string;
};

export function ProductCostsDisplay({
  ticker,
  dataset,
  costTypes,
  costValues,
  onCostValuesChange,
}: ProductCostsDisplayProps) {
  const localize = useLocalize();
  const t = useT(T);

  const CATEGORY_LABELS: Record<CostTypeCategory, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
    WITHDRAWAL: t.categoryWithdrawal,
  } as Record<CostTypeCategory, string>;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiValues, setApiValues] = useState<ApiCostValue[]>([]);

  const hasLinkedInstrument = ticker && dataset;

  // Ref to the latest costValues so per-row handlers can stay referentially
  // stable across renders — required for CostRow's React.memo to actually skip
  // re-renders when an unrelated row's value changes.
  const costValuesRef = useRef(costValues);
  costValuesRef.current = costValues;

  const apiValueByCostType = useMemo(() => {
    const m = new Map<string, ApiCostValue>();
    for (const v of apiValues) m.set(v.costTypeId, v);
    return m;
  }, [apiValues]);

  const fetchCosts = useCallback(
    async (fetchTicker: string, fetchDataset: string): Promise<ApiCostValue[]> => {
      setLoading(true);
      setError(null);

      try {
        const metadata = (await pcApi.getInstrumentMetadata({
          ticker: fetchTicker,
          dataset: fetchDataset,
        })) as Record<string, unknown>;
        const next = computeApiCostValues(metadata, costTypes, localize);
        setApiValues(next);
        return next;
      } catch (_err) {
        setError(t.failedToFetchCostData);
        const fallback = computeApiCostValues(null, costTypes, localize);
        setApiValues(fallback);
        return fallback;
      } finally {
        setLoading(false);
      }
    },
    [costTypes, localize, t.failedToFetchCostData],
  );

  useEffect(() => {
    if (!ticker || !dataset) {
      setApiValues(computeApiCostValues(null, costTypes, localize));
      return;
    }

    fetchCosts(ticker, dataset);
  }, [ticker, dataset, costTypes, localize, fetchCosts]);

  const handleOverrideChange = useCallback(
    (costTypeId: string, value: string) => {
      const current = costValuesRef.current;
      const numValue = value === "" ? null : parseFloat(value);
      const existing = current[costTypeId] ?? { calculatedValue: 0, overriddenValue: null };
      onCostValuesChange({
        ...current,
        [costTypeId]: { ...existing, overriddenValue: Number.isNaN(numValue) ? null : numValue },
      });
    },
    [onCostValuesChange],
  );

  const handleUpdateFromApi = async () => {
    if (!ticker || !dataset) return;
    const fresh = await fetchCosts(ticker, dataset);
    const newValues: Record<string, CostValueEntry> = { ...costValuesRef.current };
    for (const apiValue of fresh) {
      if (apiValue.value !== null && newValues[apiValue.costTypeId] !== undefined) {
        newValues[apiValue.costTypeId] = {
          ...newValues[apiValue.costTypeId],
          calculatedValue: apiValue.value,
        };
      }
    }
    onCostValuesChange(newValues);
  };

  const handleRemoveCost = useCallback(
    (costTypeId: string) => {
      const next = { ...costValuesRef.current };
      delete next[costTypeId];
      onCostValuesChange(next);
    },
    [onCostValuesChange],
  );

  const handleInlineAdd = useCallback(
    (costTypeId: string, fallbackValue: number | null) => {
      onCostValuesChange({
        ...costValuesRef.current,
        [costTypeId]: {
          calculatedValue: fallbackValue ?? 0,
          overriddenValue: null,
        },
      });
    },
    [onCostValuesChange],
  );

  if (costTypes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-sm text-muted-foreground">{t.noCostTypesConfigured}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with description and Update button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {hasLinkedInstrument ? (
            <>
              {t.costDataFromLinked} ({ticker}).
            </>
          ) : (
            t.noInstrumentLinked
          )}
        </div>
        {hasLinkedInstrument && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUpdateFromApi}
            disabled={loading}
            className="gap-2"
          >
            {t.fetchLatestCosts}
          </Button>
        )}
      </div>

      {/* Loading state for API data */}
      {loading && hasLinkedInstrument && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">{t.loadingApiData}</span>
        </div>
      )}

      {/* Error state for API data */}
      {error && hasLinkedInstrument && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Combined cost-types table (assigned + unassigned) */}
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.costType}</TableHead>
            <TableHead>{t.category}</TableHead>
            <TableHead className="text-right">{t.calculatedValue}</TableHead>
            <TableHead className="text-right">{t.overriddenValue}</TableHead>
            <TableActionsHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {costTypes.map((costType) => (
            <CostRow
              key={costType.id}
              costType={costType}
              apiValue={apiValueByCostType.get(costType.id)}
              entry={costValues[costType.id]}
              categoryLabel={
                CATEGORY_LABELS[costType.category as CostTypeCategory] ?? costType.category
              }
              removeLabel={t.remove}
              addLabel={t.add}
              overrideMustBeNonNegativeLabel={t.overrideMustBeNonNegative}
              onOverrideChange={handleOverrideChange}
              onRemove={handleRemoveCost}
              onAdd={handleInlineAdd}
            />
          ))}
        </TableBody>
      </Table>

      {/* Help text */}
      <p className="text-xs text-muted-foreground">
        {hasLinkedInstrument ? t.helpTextLinked : t.helpTextManual}
      </p>
    </div>
  );
}
